import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel } from "@/components/core/forms/form.tsx";
import { InputFile } from "@/components/core/forms/input-file";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useEvidenceMutation } from "../api/queries/evidence-query";
import {
	type CaseEvidence,
	type CaseEvidenceForm,
	type CaseTestimonialEvidenceSchema,
	caseEvidenceFormSchema,
	caseTestimonialEvidenceSchema,
} from "../types/evidence";

export default function CaseTestimonialEvidenceFileUploadModal({
	data,
	affidavit,
	evidenceAttachment,
	judicialAffidavit,
}: {
	data: CaseEvidence;
	affidavit?: boolean;
	evidenceAttachment?: boolean;
	judicialAffidavit?: boolean;
}) {
	const { closeModal } = useModalControl();
	const caseEvidenceMutation = useEvidenceMutation();
	const modal = useModal();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const form = useForm<CaseTestimonialEvidenceSchema>({
		resolver: zodResolver(caseTestimonialEvidenceSchema),
		defaultValues: {
			id: data?.id,
			case_id: data?.case_id,
			case_record_user_id: data?.case_record_user_id || "",
			evidence_type: data?.evidence_type,
			parties: data?.parties,
			status: data?.status || "",
			witness_name: data?.witness_name,
			description: data?.description,
			marking_status: data?.marking_status || "",
			admission_status: data?.admission_status || "",
			affidavit: null,
			stenography: null,
		},
	});

	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			try {
				setIsLoading(true);
				await caseEvidenceMutation.mutateAsync(values);
				closeModal();
			} catch (error) {
				console.error(error);
				setIsLoading(false);
			}
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "sm",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								try {
									setIsLoading(true);
									await caseEvidenceMutation.mutateAsync(values);
									closeModal();
								} catch (error) {
									console.error(error);
									setIsLoading(false);
								}
							}}
						/>
					),
				});
				closeModal();
			} catch (error) {
				console.log(error);
			}
		}
	};
	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)} encType="multipart/form-data">
				<div className="grid grid-cols-12 w-full gap-3 py-3">
					{affidavit ? (
						<div className="col-span-12 flex">
							<FormField
								control={form.control}
								name="affidavit"
								render={() => {
									return (
										<FormItem>
											<FormLabel required>Supplementary Judicial Affidavit</FormLabel>
											<FormGroup iconPosition="left">
												<InputFile
													className="!relative"
													onChange={(e) => {
														if (e.target?.files?.[0]) {
															form.setValue("affidavit", e.target?.files?.[0]);
														}
													}}
												/>
											</FormGroup>
										</FormItem>
									);
								}}
							/>
						</div>
					) : (
						!affidavit &&
						!evidenceAttachment && (
							<div className="col-span-12 flex">
								<FormField
									control={form.control}
									name="stenography"
									render={() => {
										return (
											<FormItem>
												<FormLabel required>Transcript of Stenographic Notes (TSN)</FormLabel>
												<FormGroup iconPosition="left">
													<InputFile
														className="!relative"
														onChange={(e) => {
															if (e.target?.files?.[0]) {
																form.setValue("stenography", e.target?.files?.[0]);
															}
														}}
													/>
												</FormGroup>
											</FormItem>
										);
									}}
								/>
							</div>
						)
					)}
					{evidenceAttachment && (
						<div className="col-span-12 flex">
							<FormField
								control={form.control}
								name="evidence"
								render={() => {
									return (
										<FormItem>
											{!judicialAffidavit ? <FormLabel required>Evidence Attachment</FormLabel> : <FormLabel required>Judicial Affidavit</FormLabel>}
											<FormGroup iconPosition="left">
												<InputFile
													className="!relative"
													onChange={(e) => {
														if (e.target?.files?.[0]) {
															form.setValue("evidence", e.target?.files?.[0]);
														}
													}}
												/>
											</FormGroup>
										</FormItem>
									);
								}}
							/>
						</div>
					)}
				</div>
				<div className="flex flex-row justify-between w-full gap-4 mt-2">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" type="submit" variant="default" loading={isLoading}>
						Submit
					</Button>
				</div>
			</form>
		</Form>
	);
}
